import { buildTitle } from "@/lib/seo-quality";
import { clampDescription } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Phone, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { PageHeader } from "./fordon";
import { getArModell, getYearContext, AR_MODELLER } from "@/data/husbil-pris-ar";
import { getArsmodellDjup } from "@/data/husbil-arsmodell-djup";
import { assessArsmodell } from "@/lib/seo-families/arsmodell";
import { robotsMeta } from "@/lib/seo-quality";

export const Route = createFileRoute("/husbil-arsmodell/$ar")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const ar = getArModell(params.ar);
    if (!ar) throw notFound();
    const ctx = getYearContext(ar.year);
    return { ar, ctx, djup: getArsmodellDjup(ar.year) ?? null, quality: assessArsmodell(ctx) };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { ar, ctx, djup, quality } = loaderData;
    const url = `https://www.akhusbilfritid.se/husbil-arsmodell/${ar.slug}`;
    const title = buildTitle(`Husbil årsmodell ${ar.year} — vad du får och vad du bör kolla`);
    const desc = `Husbil ${ar.year}: teknik, priser och vad som är typiskt för årsmodellen. ${ctx.headline}. Aktuellt lager hos AK Husbil i Kalix.`;
    return {
      meta: [
        { title },
        { name: "description", content: clampDescription(desc) },
        ...robotsMeta(quality),
        { property: "og:title", content: title },
        { property: "og:description", content: clampDescription(desc) },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: `Vad är typiskt för husbil årsmodell ${ar.year}?`,
                acceptedAnswer: { "@type": "Answer", text: ctx.tech },
              },
              {
                "@type": "Question",
                name: `Vad kostar en husbil årsmodell ${ar.year}?`,
                acceptedAnswer: { "@type": "Answer", text: ctx.priceRange },
              },
              {
                "@type": "Question",
                name: `Vem passar en husbil från ${ar.year}?`,
                acceptedAnswer: { "@type": "Answer", text: ctx.perfectFor },
              },
              {
                "@type": "Question",
                name: `Vad är vanliga fel på husbil ${ar.year}?`,
                acceptedAnswer: { "@type": "Answer", text: ctx.vanligaFel.join(". ") + "." },
              },
              ...(djup
                ? djup.faq.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  }))
                : []),
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="max-w-2xl mx-auto p-8 text-center">
      <h1 className="text-2xl font-bold mb-4">Årsmodellen finns inte</h1>
      <Link to="/husbilar-till-salu" className="text-primary underline">Se alla husbilar</Link>
    </div>
  ),
  component: ArPage,
});

function ArPage() {
  const { ar, ctx, djup } = Route.useLoaderData();
  return (
    <>
      <PageHeader
        label="Årsmodell-guide"
        title={<>Husbil årsmodell {ar.year}</>}
        sub={`${ar.ageYears === 0 ? "Årets modeller" : `Ca ${ar.ageYears} år gamla`} — ${ctx.headline.toLowerCase()}`}
      />
      <main className="max-w-4xl mx-auto px-4 py-10 space-y-10">
        <section>
          <h2 className="text-2xl font-bold mb-3">Vad är typiskt för husbilar från {ar.year}?</h2>
          <p className="text-lg leading-relaxed">{ctx.tech}</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3">Det här var nytt {ar.year}</h2>
          <p className="text-lg leading-relaxed">{ctx.nytt}</p>
        </section>

        <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border p-5">
            <div className="text-sm text-muted-foreground mb-1">Prisintervall</div>
            <div className="font-bold">{ctx.priceRange}</div>
          </div>
          <div className="rounded-lg border p-5">
            <div className="text-sm text-muted-foreground mb-1">Typisk mätarställning</div>
            <div className="font-bold">{ctx.typiskMil}</div>
          </div>
          <div className="rounded-lg border p-5">
            <div className="text-sm text-muted-foreground mb-1">Värdeminskning</div>
            <div className="font-bold">{ctx.vardeminskning}</div>
          </div>
          <div className="rounded-lg border p-5">
            <div className="text-sm text-muted-foreground mb-1">Ålder 2026</div>
            <div className="font-bold">{ar.ageYears === 0 ? "Ny bil" : `${ar.ageYears} år`}</div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3">Marknadsläget för {ar.year} just nu</h2>
          <p className="leading-relaxed">{ctx.marknad}</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Det vi oftast hittar på {ar.year}-bilar</h2>
          <ul className="space-y-2">
            {ctx.vanligaFel.map((f: string) => (
              <li key={f} className="flex gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-600 shrink-0 mt-1" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            Bygger på vad vi själva ser vid fukt- och gasoltest i verkstaden i Kalix.
          </p>
        </section>

        <section className="rounded-xl bg-muted/40 border p-6">
          <h2 className="text-xl font-bold mb-2">Garantiläge {ar.year}</h2>
          <p>{ctx.garanti}</p>
        </section>

        {djup && (
          <>
            <section className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-lg border p-5">
                <div className="text-sm text-muted-foreground mb-1">Driftkostnad</div>
                <div className="font-semibold">{djup.driftkostnad}</div>
              </div>
              <div className="rounded-lg border p-5">
                <div className="text-sm text-muted-foreground mb-1">Försäkring</div>
                <div className="font-semibold">{djup.forsakring}</div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">Besiktning och service</h2>
              <p className="leading-relaxed">{djup.besiktning}</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">Förhandlingsläget för {ar.year}</h2>
              <p className="leading-relaxed">{djup.forhandling}</p>
              <h3 className="font-bold mt-5 mb-2">Värt att uppgradera</h3>
              <p className="leading-relaxed text-muted-foreground">{djup.uppgradera}</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Checklista för {ar.year}</h2>
              <ul className="space-y-2">
                {djup.checklista.map((c: string) => (
                  <li key={c} className="flex gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-1" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Vanliga frågor om årsmodell {ar.year}</h2>
              <div className="space-y-4">
                {djup.faq.map((f: { q: string; a: string }) => (
                  <div key={f.q} className="rounded-lg border p-5">
                    <h3 className="font-bold mb-2">{f.q}</h3>
                    <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        <section className="rounded-xl bg-muted/40 border p-6">
          <h2 className="text-xl font-bold mb-2">Passar för</h2>
          <p>{ctx.perfectFor}</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Jämför andra årsmodeller</h2>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {AR_MODELLER.filter((a) => a.slug !== ar.slug).map((a) => (
              <Link
                key={a.slug}
                to="/husbil-arsmodell/$ar"
                params={{ ar: a.slug }}
                className="rounded-lg border p-3 text-center hover:border-primary hover:bg-muted/40 text-sm font-semibold"
              >
                {a.year}
              </Link>
            ))}
          </div>
        </section>

        <section className="rounded-xl border-2 border-primary/20 bg-muted/40 p-6">
          <h2 className="text-xl font-bold mb-2">Se husbilar från {ar.year} i lager</h2>
          <p className="text-muted-foreground mb-4">Filtrera på år {ar.year} i vår lagerlista på Fyndanu.</p>
          <Link to="/husbilar-till-salu" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90">
            Se alla husbilar <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        <section className="rounded-xl bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Ring för rådgivning</h2>
          <p className="mb-6 opacity-90">Vi har hjälpt kunder välja rätt årsmodell sedan 2004.</p>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background text-foreground font-semibold hover:opacity-90">
            <Phone className="w-4 h-4" /> 070-862 97 43
          </a>
        </section>
      </main>
    </>
  );
}
